import { ConfirmationDialog, PoolFormattedMessage } from '@synergy/components';
import { FormattedMessage } from 'react-intl';
import { PaloColumn, PaloListView } from '@synergy/components-palo';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import * as PaymentTermsActions from './store/payment-terms-actions';
import { PaymentTerm } from '../../../../common/models/payment-term';
import { TranslatedField } from '../../components/translated-field';
import { TranslationPill } from '../../components/translation-pill';
import { paymentTermsSelector } from './store/payment-terms-selector';
import { selectedLanguageSelector } from '../../../../common/selectors/selected-language-selectors';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

export interface PaymentTermsOverviewProps {
    onEdit: (unit: PaymentTerm) => void;
}

export const PaymentTermsOverview = (props: PaymentTermsOverviewProps) => {
    const dispatch = useThunkDispatch();
    const selectedLanguage = useAppSelector(selectedLanguageSelector);
    const paymentTerms = useAppSelector(paymentTermsSelector);
    const availableLocales = useAppSelector(state => state.supportedLocales);
    const [searchParams, setSearchParams] = useSearchParams(new URLSearchParams({ sort: 'id', order: 'asc' }));
    const [deleteModalData, setDeleteModalData] = useState<PaymentTerm | null>(null);

    // The list sorts on the server, so the chosen column travels in the address.
    // Until one is chosen the list claims no order of its own — an arrow over a
    // column the server is not sorting by says something untrue.
    const sortColumn = searchParams.get('sort');
    const sort = sortColumn
        ? { key: sortColumn, order: ('desc' === searchParams.get('order') ? 'desc' : 'asc') as 'asc' | 'desc' }
        : undefined;

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const columns: PaloColumn<PaymentTerm>[] = [
        {
            key: 'id',
            type: 'documentNumber',
            sortKey: 'id',
            head: <FormattedMessage id="app.common.id" />,
            cell: paymentTerm => paymentTerm.id,
        },
        {
            key: 'text',
            type: 'name',
            head: <FormattedMessage id="app.configuration.paymentTerms.text" />,
            cell: paymentTerm => (
                <TranslatedField
                    value={paymentTerm.text}
                    selectedLocale={selectedLanguage}
                    fallback={`ID: ${paymentTerm.id}`}
                />
            ),
        },
        {
            key: 'days',
            type: 'count',
            head: <FormattedMessage id="app.configuration.paymentTerms.days" />,
            cell: paymentTerm => paymentTerm.days,
        },
        {
            key: 'discount',
            type: 'percent',
            head: <FormattedMessage id="app.configuration.paymentTerms.discount" />,
            cell: paymentTerm => `${paymentTerm.discount} %`,
        },
        {
            key: 'netDays',
            type: 'count',
            head: <FormattedMessage id="app.configuration.paymentTerms.netDays" />,
            cell: paymentTerm => paymentTerm.netDays,
        },
        {
            key: 'isTranslated',
            type: 'flag',
            head: <FormattedMessage id="app.table.column.translation" />,
            cell: item => <TranslationPill value={item.text} locales={availableLocales} />,
        },
    ];

    useEffect(() => {
        dispatch(PaymentTermsActions.fetchPaymentTerms(searchParams));
    }, [dispatch, searchParams]);

    const handleDelete = () => {
        if (!deleteModalData?.id || deleteModalData.id < 0) {
            return;
        }
        dispatch(PaymentTermsActions.deletePaymentTerm(deleteModalData.id));
        setDeleteModalData(null);
    };

    return (
        <>
            <PaloListView<PaymentTerm, number>
                embedded={true}
                columns={columns}
                items={paymentTerms.items}
                getRowId={paymentTerm => paymentTerm.id}
                emptyLabel="Keine Zahlungsbedingungen."
                rowActions={paymentTerm => [
                    { label: <FormattedMessage id="app.common.edit" />, onClick: () => props.onEdit(paymentTerm) },
                    {
                        label: <FormattedMessage id="app.common.delete" />,
                        onClick: () => setDeleteModalData(paymentTerm),
                    },
                ]}
                sort={sort}
                onSortChange={(key, order) => updateParams({ sort: key, order, page: '1' })}
                pagination={{
                    count: paymentTerms.total,
                    page: Number(searchParams.get('page') ?? 1),
                    limit: Number(searchParams.get('limit') ?? 50),
                    onPageChange: page => updateParams({ page: String(page) }),
                    onLimitChange: limit => updateParams({ limit: String(limit), page: '1' }),
                }}
            />
            <ConfirmationDialog
                isVisible={!!deleteModalData}
                title={<FormattedMessage id="app.configuration.paymentTerms.delete" />}
                action={<FormattedMessage id="app.common.delete" />}
                onConfirm={handleDelete}
                onCancel={() => setDeleteModalData(null)}
            >
                <PoolFormattedMessage id="app.configuration.paymentTerms.delete.text" />
            </ConfirmationDialog>
        </>
    );
};
